import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useData } from '../contexts/DataContext'
import QuickAdd from '../components/QuickAdd'
import HistoryList from '../components/HistoryList'
import { ensureSubs, formatDate, renderPayload } from '../utils/helpers'

const Kiosk = ({ onShowSignup }) => {
  const { searchMembers, kioskExercises, fetchKioskExercises, fetchResults, results, createResult, deleteResult } = useData()
  const location = useLocation()
  const [query, setQuery] = useState('')
  const [memberId, setMemberId] = useState(null)
  const [selectedMemberData, setSelectedMemberData] = useState(null)
  const [activeExId, setActiveExId] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [expandedSubs, setExpandedSubs] = useState({})
  const [memberResults, setMemberResults] = useState([])

  // Reset to search view when navigating to kiosk home
  useEffect(() => {
    if (location.pathname === '/' && memberId) {
      handleBackToSearch()
    }
  }, [location.pathname])

  // Fetch kiosk exercises on mount
  useEffect(() => {
    fetchKioskExercises()
  }, [])

  // Search members when query changes
  useEffect(() => {
    if (query.trim().length > 0) {
      const search = async () => {
        const results = await searchMembers(query.trim())
        setSearchResults(results)
      }
      search()
    } else {
      setSearchResults([])
    }
  }, [query])

  // Fetch member results when member is selected
  useEffect(() => {
    if (memberId) {
      fetchResults({ memberId })
    }
  }, [memberId])

  // Update member results when results change
  useEffect(() => {
    if (memberId) {
      setMemberResults(results.filter(r => r.memberId._id === memberId))
    }
  }, [results, memberId])

  // Reset states when member changes
  useEffect(() => {
    setActiveExId(null)
    setExpandedSubs({})
  }, [memberId])

  const selectedMember = useMemo(() => {
    return selectedMemberData
  }, [selectedMemberData])

  const handleMemberSelect = (member) => {
    setMemberId(member._id)
    setSelectedMemberData(member)
    setQuery('')
    setSearchResults([])
  }

  const handleBackToSearch = () => {
    setMemberId(null)
    setSelectedMemberData(null)
    setQuery('')
    setActiveExId(null)
    setMemberResults([])
  }

  const handleAddResult = async (resultData) => {
    const result = await createResult({
      memberId,
      exerciseId: resultData.exerciseId,
      subId: resultData.subId,
      payload: resultData.payload
    })
    
    if (result.success) {
      // Refresh results for this member
      fetchResults({ memberId })
    } else {
      alert(result.error)
    }
  }

  const handleDeleteResult = async (resultId) => {
    if (!confirm('Are you sure you want to delete this entry?')) return
    
    const result = await deleteResult(resultId)
    if (result.success) {
      fetchResults({ memberId })
    } else {
      alert(result.error)
    }
  }

  const toggleExpand = (key) => {
    setExpandedSubs(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const getResultsForSub = (exerciseId, subId) => {
    return memberResults
      .filter(r => r.exerciseId._id === exerciseId && r.subId === subId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }

  if (!memberId) {
    return (
      <div className="grid" style={{ gap: 20 }}>
        <div className="hero card">
          <div className="grid" style={{ gap: 20, maxWidth: 760, margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <img className="kiosk-hero-logo" src="/Bfit_Text_Logo-01.png" alt="BFIT" />
            <div style={{ position: 'relative' }}>
              <input 
                autoFocus 
                placeholder="SEARCH ATHLETE NAME" 
                value={query} 
                onChange={e => setQuery(e.target.value)}
                style={{
                  fontSize: '18px',
                  padding: '16px 20px',
                  borderRadius: '12px',
                  border: '2px solid rgba(232, 119, 34, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--bfit-white)',
                  textAlign: 'center',
                  letterSpacing: '1px',
                  fontWeight: '600'
                }}
              />
            </div>
            
            {query && (
              <div className="list" style={{ 
                borderRadius: '12px',
                border: '1px solid rgba(232, 119, 34, 0.3)',
                background: 'rgba(255, 255, 255, 0.02)',
                backdropFilter: 'blur(10px)'
              }}>
                {searchResults.map(member => (
                  <div 
                    key={member._id} 
                    className="item row" 
                    onClick={() => handleMemberSelect(member)}
                    style={{ 
                      cursor: 'pointer',
                      padding: '16px 20px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '16px' }}>{member.name}</div>
                    <span className="pill" style={{ 
                      background: 'var(--bfit-orange)', 
                      color: '#000', 
                      fontWeight: '700',
                      padding: '6px 12px'
                    }}>SELECT</span>
                  </div>
                ))}
                {searchResults.length === 0 && (
                  <div className="item muted" style={{ textAlign: 'center', padding: '20px' }}>
                    No athletes found
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button 
            className="fab" 
            title="Create your account" 
            aria-label="Create your account" 
            onClick={onShowSignup}
            style={{
              background: 'linear-gradient(135deg, var(--bfit-orange) 0%, #ff8f50 100%)',
              boxShadow: '0 8px 24px rgba(232, 119, 34, 0.4)'
            }}
          >
            +
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="grid kiosk-grid">
      {/* Left side - Exercise list */}
      <div className="card kiosk-left-panel" style={{ gap: 0 }}>
        <div className="kiosk-athlete-header">
          <div className="row">
            <div>
              <div className="athlete-name-label">ATHLETE'S NAME</div>
              <div className="athlete-name-display">
                {selectedMember?.name?.toUpperCase()}
              </div>
            </div>
            <button className="btn ghost slim" onClick={handleBackToSearch}>
              ← Back
            </button>
          </div>
        </div>
        
        <div className="kiosk-exercise-section">
          <div className="exercise-badge">
            PICK YOUR EXERCISE
          </div>
          
          <div className="list ex-list">
            {kioskExercises.map(exercise => (
              <div 
                key={exercise._id} 
                className={`item ${activeExId === exercise._id ? 'active' : ''}`} 
                onClick={() => setActiveExId(exercise._id)}
                style={{ cursor: 'pointer' }}
              >
                {exercise.name.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Exercise details */}
      <div className="card kiosk-right-panel" style={{ gap: 16 }}>
        {!activeExId ? (
          <div style={{ 
            color: '#6c757d', 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'linear-gradient(135deg, rgba(232, 119, 34, 0.05) 0%, rgba(232, 119, 34, 0.02) 100%)',
            borderRadius: '16px',
            border: '2px dashed rgba(232, 119, 34, 0.2)'
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              filter: 'grayscale(0.3)'
            }}>💪</div>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px'
            }}>
              Ready to Train?
            </div>
            <div style={{ 
              fontSize: '14px',
              color: '#6c757d',
              lineHeight: '1.5'
            }}>
              Select an exercise from the left panel<br />
              to begin your workout session.
            </div>
          </div>
        ) : (
          <ExerciseDetails 
            exercise={kioskExercises.find(e => e._id === activeExId)}
            memberResults={memberResults}
            onAddResult={handleAddResult}
            onDeleteResult={handleDeleteResult}
            expandedSubs={expandedSubs}
            onToggleExpand={toggleExpand}
            onBack={() => setActiveExId(null)}
          />
        )}
      </div>
    </div>
  )
}

const ExerciseDetails = ({ 
  exercise, 
  memberResults, 
  onAddResult, 
  onDeleteResult, 
  expandedSubs, 
  onToggleExpand, 
  onBack 
}) => {
  if (!exercise) return <div className="muted" style={{ color: '#333' }}>Exercise not found.</div>

  const subs = ensureSubs(exercise).filter(s => s.showInKiosk !== false)
  const MAX_ROWS = 5

  const getResultsForSub = (subId) => {
    return memberResults
      .filter(r => r.exerciseId._id === exercise._id && r.subId === subId)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }

  function parseTime(str) {
    if (!str) return Infinity; // no time = worst

    const [minPart, rest] = str.split(":");

    const min = Number(minPart) || 0;

    let sec = 0;
    let ms = 0;

    if (rest) {
      const [secPart, msPart] = rest.split(".");
      sec = Number(secPart) || 0;
      ms = Number(msPart) || 0;
    }

    // convert to total milliseconds
    return (min * 60 * 1000) + (sec * 1000) + ms;
  }

  return (
    <div className="grid" style={{ gap: 20, padding: '20px' }}>
      {/* Exercise Header */}
      <div className="row" style={{ 
        alignItems: 'center', 
        paddingBottom: '16px', 
        borderBottom: '2px solid rgba(232, 119, 34, 0.2)',
        background: 'linear-gradient(135deg, rgba(232, 119, 34, 0.05) 0%, rgba(232, 119, 34, 0.02) 100%)',
        padding: '16px 20px',
        borderRadius: '12px',
        margin: '-8px -8px 8px -8px'
      }}>
        <div style={{ flex: 1 }}>
          <div className="big" style={{ 
            fontWeight: 900, 
            color: '#212529',
            fontSize: 'clamp(18px, 4vw, 24px)',
            marginBottom: '4px',
            letterSpacing: '1px'
          }}>
            {exercise.name.toUpperCase()}
          </div>
          <div style={{ 
            color: '#6c757d',
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {exercise.mode} • {subs.length} Subcategor{subs.length === 1 ? 'y' : 'ies'}
          </div>
        </div>
        <button className="btn ghost slim" onClick={onBack} style={{ 
          color: '#6c757d', 
          borderColor: '#dee2e6',
          padding: '8px 16px',
          fontWeight: '600'
        }}>
          ← All exercises
        </button>
      </div>

      {/* Workout description */}
      {exercise.mode === 'workout' && exercise.desc && (
        <div style={{
          fontSize: 'clamp(14px, 2.2vw, 16px)',
          color: '#495057',
          backgroundColor: 'rgba(232, 119, 34, 0.08)',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '2px solid rgba(232, 119, 34, 0.15)',
          marginTop: '-8px',
          marginBottom: '8px',
          fontStyle: 'italic',
          lineHeight: '1.5',
          boxShadow: '0 2px 8px rgba(232, 119, 34, 0.1)'
        }}>
          <strong style={{ color: '#212529', fontStyle: 'normal' }}>Workout Description:</strong><br />
          {exercise.desc}
        </div>
      )}

      {subs.map(sub => {
        const rowsAll = getResultsForSub(sub._id || sub.id)
        const last = rowsAll[0] || null

        // Get top record for strength/workout
        const topRec = rowsAll.reduce((best, cur) => {
          if (!best) return cur;
          
          const w = (cur.payload?.weight != null) ? Number(cur.payload.weight) : -Infinity
          const bw = (best?.payload?.weight != null) ? Number(best.payload.weight) : -Infinity
          if (w > bw) {
            return cur;
          } else if (w < bw) {
            return best;
          }
        
          // If weights are equal (both null or same value), compare by time
          const tCur = parseTime(cur.payload?.time);
          const tBest = parseTime(best.payload?.time);
        
          return (tCur < tBest) ? cur : best;
        }, null)

        const key = `${exercise._id}:${sub._id || sub.id}`
        const isExpanded = !!expandedSubs[key]

        let rows = isExpanded ? rowsAll : rowsAll.slice(0, MAX_ROWS)
        if (!isExpanded && topRec && !rows.some(r => r._id === topRec._id)) {
          rows = [topRec, ...rows]
        }

        return (
          <div key={sub._id || sub.id} className="card subcard" style={{ 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)', 
            color: '#212529',
            border: '2px solid rgba(232, 119, 34, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            position: 'relative',
          }}>
            {/* Orange top accent */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, var(--bfit-orange) 0%, #ff8f50 100%)'
            }} />
            
            <div className="row header" style={{ 
              alignItems: 'flex-end', 
              paddingBottom: '16px', 
              borderBottom: '2px solid rgba(232, 119, 34, 0.1)',
              marginTop: '8px'
            }}>
              <div style={{ flex: 1 }}>
                <div className="big" style={{ 
                  fontWeight: 900, 
                  color: '#212529', 
                  marginBottom: '6px',
                  fontSize: 'clamp(16px, 3vw, 20px)',
                  letterSpacing: '0.5px'
                }}>
                  {sub.name.toUpperCase()}
                </div>
                <div className="muted" style={{ 
                  color: '#6c757d', 
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {exercise.mode === 'strength' ? 'Weight • Reps • Sets • Notes' :
                   exercise.mode === 'cardio' ? (exercise.cardioMetric === 'distance' ? 'Distance • Time • Notes' : 'Calories • Time • Notes') :
                   Object.entries(sub?.fields || {}).filter(([k, v]) => v !== false).map(([k]) => {
                     if (k === 'primary') return exercise.cardioMetric === 'distance' ? 'Distance' : 'Calories'
                     return k[0].toUpperCase() + k.slice(1)
                   }).join(' • ')}
                </div>
              </div>
            </div>

            {/* Quick Add Section - More Prominent */}
            <div style={{ margin: '20px 0 16px 0' }}>
              <QuickAdd 
                exercise={exercise} 
                sub={sub} 
                onAdd={(payload) => onAddResult({
                  exerciseId: exercise._id,
                  subId: sub._id || sub.id,
                  payload
                })} 
                last={last} 
              />
            </div>

            {/* Performance Info Bar */}
            <div className="row" style={{ 
              background: 'linear-gradient(135deg, rgba(232, 119, 34, 0.12) 0%, rgba(232, 119, 34, 0.06) 100%)', 
              padding: '14px 18px', 
              borderRadius: '10px',
              margin: '12px 0 16px 0',
              border: '2px solid rgba(232, 119, 34, 0.2)',
              boxShadow: '0 2px 8px rgba(232, 119, 34, 0.1)'
            }}>
              {
              // exercise.mode === 'cardio' ? (
              //   last ? (
              //     <div style={{ color: '#495057', display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
              //       <span style={{ fontSize: '16px' }}>🏃‍♂️</span>
              //       <div style={{ flex: 1 }}>
              //         <strong style={{ color: '#212529' }}>Last Session:</strong> {renderPayload(last.payload, exercise)} 
              //         <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
              //           {formatDate(last.createdAt)}
              //         </div>
              //       </div>
              //     </div>
              //   ) : (
              //     <div className="muted" style={{ color: '#6c757d', textAlign: 'center', width: '100%' }}>
              //       <span style={{ fontSize: '16px' }}>🚀</span> Ready for your first entry!
              //     </div>
              //   )
              // ) : (
                topRec ? (
                  <div style={{ color: '#495057', display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                    <span style={{ fontSize: '16px' }}>🏆</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: '#212529' }}>Personal Best:</strong> {renderPayload(topRec.payload, exercise)} 
                      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>
                        {formatDate(topRec.createdAt)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="muted" style={{ color: '#6c757d', textAlign: 'center', width: '100%' }}>
                    <span style={{ fontSize: '16px' }}>🚀</span> Ready to set your first record!
                  </div>
                )
              }
            </div>

            {/* Workout History */}
            <div style={{ marginTop: '8px' }}>
              <HistoryList 
                rows={rows}
                exercise={exercise}
                topWeight={topRec?.payload?.weight || 0}
                onDelete={onDeleteResult}
                fastestTime={topRec?.payload?.time || ''}
              />
            </div>

            {/* Show More/Less Toggle */}
            {rowsAll.length > MAX_ROWS && (
              <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
                <button
                  className="btn ghost tiny"
                  onClick={() => onToggleExpand(key)}
                  aria-expanded={isExpanded}
                  style={{ 
                    color: '#6c757d', 
                    borderColor: '#dee2e6',
                    padding: '8px 16px',
                    fontWeight: '600',
                    fontSize: '12px',
                    borderRadius: '20px'
                  }}
                >
                  {isExpanded ? (
                    <>↑ Show less</>
                  ) : (
                    <>↓ Show all ({rowsAll.length} entries)</>
                  )}
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default Kiosk
